import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { Link } from 'react-router-dom';
import { FormattedDate, useIntl } from 'react-intl';
import { Badge, Badges } from '@tovia/man-ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCameraAlt, faImages } from '@fortawesome/pro-regular-svg-icons';
import { ImageDetailsPanel } from '../../components/panels/ImageDetailsPanel';
import { formatDateDefaultOptions } from '../../utils/converters';
import DownloadDropdown, { Download } from '../../components/dropdowns/DownloadDropdown/DownloadDropdown';
import { UpgradeDialog } from '../../components/UpgradeDialog/UpgradeDialog';
import FavoriteButton from '../FavoriteButton/FavoriteButton';
import { Panel } from '../../components/Placeholders/Panels/Panel';
import { useSelector } from '../../redux';
import { MOBILE_LANDSCAPE } from '../../constants/mediaQueries';
import { Gallery } from 'src/@types/Gallery';
import { generateGalleryAlt } from 'src/client/utils/generateGalleryAlt';
import { useGetGalleryCoverUrl } from 'src/client/helpers/urls/useGetGalleryCoverUrl';

const quality2title = {
  high: 'High Zip',
  med: 'Medium Zip',
  low: 'Low Zip',
  cover: 'Cover',
  clean: 'Clean Cover',
};

interface Props {
  allowDownloads: boolean;
  gallery: Gallery;
  className?: string;
}

export const GalleryDetailsPanel = (props: Props) => {
  const { gallery, allowDownloads, className } = props;
  const contentCdnUrl = useSelector((state) => state.app.contentCdnUrl);
  const config = useSelector((state) => state.app.config);
  const sites = useSelector((state) => state.sites.items);
  const favoriteInfo = useSelector((state) => state.favoriteInfo);
  const user = useSelector((state) => state.auth.user);
  const { downloadCounts } = useSelector((state) => state.galleryPermissions);

  const intl = useIntl();
  const getGalleryCover = useGetGalleryCoverUrl();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const { UUID, type, path, publishedAt, name, favoriteCount, models } = gallery;

  const photoCount = gallery.photos?.total ?? 0;
  const megapixels = gallery.photos?.megapixels ?? 0;

  const galleryCover = getGalleryCover(contentCdnUrl, gallery, { clean: config.cleanGalleryCovers });
  const coverUrl = galleryCover.url;

  const badges = [
    ...(photoCount > 0 ? [<Badge key="images" text={photoCount} icon={<FontAwesomeIcon icon={faImages} />} />] : []),
    ...(megapixels > 0
      ? [<Badge key="megapixels" text={`${megapixels} MP`} icon={<FontAwesomeIcon icon={faCameraAlt} />} />]
      : []),
  ];

  let downloads: Download[] = [];
  if (allowDownloads && !gallery.downloadsDisabled) {
    const fileDownloads = gallery?.files?.sizes?.zips?.map((download) => ({
      ...download,
      id: download.quality,
      link: `/api/download-media/${gallery.UUID}/photos/${download.quality}`,
      title: quality2title[download.quality],
      isDownloadMedia: true,
    }));

    const coverDownloads = gallery?.files?.sizes?.relatedPhotos?.map((download) => ({
      id: download.id,
      link: `${
        getGalleryCover(contentCdnUrl, gallery, {
          clean: download.id === 'clean',
        }).url
      }?filename=${encodeURIComponent(
        `${gallery.name} - ${quality2title[download.id]} - ${
          sites.find((cSite) => cSite.UUID === gallery.siteUUID)?.name
        }`,
      )}.jpg`,
      title: quality2title[download.id],
    }));
    downloads = [...(fileDownloads || []), ...(coverDownloads || [])];
  }

  const handleTrialClick = (link) => {
    if (downloadCounts.allowedToDownload) {
      window.location.href = link;
    } else {
      setShowUpgradeModal(true);
    }
  };

  const galleryDetailsContent = (
    <>
      <GalleryImageDetailsPanel
        isBlurred={galleryCover.blurImage}
        imgSrc={coverUrl}
        imgAlt={generateGalleryAlt(name, models)}
        className={className}
      >
        <GalleryName>
          <Link to={path}>{name}</Link>
        </GalleryName>
        <Timestamp>
          <FormattedDate {...formatDateDefaultOptions} month="short" value={publishedAt} />
        </Timestamp>
        <FavoriteButton favoriteCount={favoriteCount} objectType={type.toUpperCase()} objectUUID={UUID} />
        {downloads.length > 0 && (
          <>
            <div className="clear" />
            <StyledDropdownDownload
              label={`${intl.formatMessage({ id: 'gallery.download', defaultMessage: 'Download' })}:`}
              downloads={downloads}
              onItemClick={(link) => handleTrialClick(link)}
              iconType="photo"
            />
          </>
        )}
        {user && gallery.downloadsDisabled && <DownloadsDisabled>View online only</DownloadsDisabled>}
        {badges.length > 0 && <StyledBadges>{badges}</StyledBadges>}
      </GalleryImageDetailsPanel>
      <UpgradeDialog
        show={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        eventTrigger="trial-user-download-gallery"
      />
    </>
  );

  const galleryDetailsPlaceholder = <Panel width={100} className={className} />;

  return !user || favoriteInfo.loaded ? galleryDetailsContent : galleryDetailsPlaceholder;
};

const DownloadsDisabled = styled.div`
  margin-top: 10px;
`;

const GalleryImageDetailsPanel = styled(ImageDetailsPanel)`
  img {
    @media ${MOBILE_LANDSCAPE} {
      max-height: 165px;
    }
  }
`;

const Timestamp = styled.span`
  display: block;
  font-size: 0.9rem;
  color: ${(props) => props.theme.primary7};
`;

const StyledDropdownDownload = styled(DownloadDropdown)`
  margin-top: 10px;

  & > div {
    padding-left: 0;
  }
`;

const StyledBadges = styled(Badges)`
  margin-top: 10px;
`;

const GalleryName = styled.h1`
  margin-top: 0;
  font-weight: 100;
  font-size: 1.65rem;
  margin-bottom: 10px;
  white-space: pre-wrap;

  a,
  a:active,
  a:not(.btn):hover,
  a:visited {
    font-weight: 100;
    text-decoration: none;
    font-size: 1.6rem;
  }
`;
